import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { trackById } from 'src/app/core/utils/track-by';
import {
  BillingCycle,
  ChargeType,
  DiscountType,
  ServiceCategory,
  Status,
} from 'src/app/shared/enums/enums';
import { BillableServiceResponse } from '../../../features/admin-portal/modules/billable-services/models';
import { BillableServiceService } from '../../../features/admin-portal/modules/billable-services/services/billable-service.service';
import { transformSubscriptionPackageToState } from '../../../features/admin-portal/modules/company-management/components/add-company-v2/add-company-v2-state.mapper';
import {
  AddonDisplayInfo,
  AddonState,
  AppAccessState,
  AppBasePriceInfo,
  PricingTier,
  SubscriptionPackageState,
} from '../../../features/admin-portal/modules/company-management/components/add-company-v2/add-company-v2-state.model';
import { SubscriptionPackageResponse } from '../../../features/admin-portal/modules/subscription-plans/models';
import { SubscriptionPlanService } from '../../../features/admin-portal/modules/subscription-plans/services/subscription-plan.service';
import { AppLiteState } from '../../../features/admin-portal/modules/subscription/models/subscription.models';

/**
 * Shared component for configuring subscription plans across the application.
 * Handles app selection, plan selection, seat configuration, add-ons, and pricing calculations.
 *
 * Used in:
 * - Add Company workflow (Step 2)
 * - Manage Subscription (Change/Add Plan)
 */
@Component({
  selector: 'app-subscription-configurator',
  templateUrl: './subscription-configurator.component.html',
  styleUrls: ['./subscription-configurator.component.css'],
  standalone: false,
})
export class SubscriptionConfiguratorComponent implements OnInit, OnChanges {
  trackById = trackById;

  // ============================================================================
  // INPUT PROPERTIES (Configuration from parent components)
  // ============================================================================

  /** List of all available applications that can be included in a subscription */
  @Input() availableApplications: AppLiteState[] = [];

  /** The billing cycle to use by default (Monthly, Yearly Paid Monthly, Yearly Paid Yearly) */
  @Input() initialBillingCycle: BillingCycle = BillingCycle.Monthly;

  /** IDs of applications that should be pre-selected (useful for editing existing subscriptions) */
  @Input() preSelectedApplicationIds: number[] = [];

  /** Whether to show the app selection step (set to false if apps are already determined) */
  @Input() showApplicationSelectionStep: boolean = true;

  /** Whether to show detailed pricing breakdown information */
  @Input() showDetailedPricingBreakdown: boolean = true;

  /** Whether to allow users to reset their plan selection */
  @Input() allowResetConfiguration: boolean = true;

  /** Whether to automatically confirm app selection (skip the "Continue to Plans" button) */
  @Input() autoConfirmApplicationSelection: boolean = false;

  /** Whether to enable trial period configuration (show trial options) */
  @Input() enableTrialConfiguration: boolean = true;

  /** Default trial duration in days */
  @Input() defaultTrialDurationDays: number = 30;

  /**
   * When provided, auto-selects this plan after plans load and locks Section 2.
   * Used by the Manage Add-ons flow to pre-select the current active plan.
   * Seat values from this plan are overlaid onto the freshly-loaded plan data.
   */
  @Input() preSelectedPlanPackage: SubscriptionPackageState | null = null;

  /**
   * When true, plan cards in Section 2 become non-interactive and the billing
   * cycle selector in the Pricing Summary is disabled. Used by Manage Add-ons
   * flow to prevent the user from switching the plan or billing cycle.
   */
  @Input() lockPlanSelection: boolean = false;

  // ============================================================================
  // OUTPUT EVENTS (Communication to parent components)
  // ============================================================================

  /** Emitted when a complete subscription configuration is ready (plan + seats + add-ons) */
  @Output() subscriptionPlanConfigured = new EventEmitter<{
    plan: SubscriptionPackageState;
    billingCycle: BillingCycle;
    totalPrice: number;
    totalSeats: number;
    trialEnabled: boolean;
    trialEndDate: string | null;
  }>();

  /** Emitted when the user resets the configuration */
  @Output() configurationReset = new EventEmitter<void>();

  /** Emitted when the validity of the configuration changes */
  @Output() configurationValidityChanged = new EventEmitter<boolean>();

  /** Emitted when the selected billing cycle changes */
  @Output() billingCycleChanged = new EventEmitter<BillingCycle>();

  // ============================================================================
  // COMPONENT STATE - App Selection
  // ============================================================================

  /** Internal working copy of available apps (with selection state) */
  workingApplicationsList: AppLiteState[] = [];

  /** Set of currently selected application IDs */
  selectedApplicationIds: Set<number> = new Set();

  /** Whether the app selection has been confirmed and locked */
  areApplicationsConfirmedAndLocked: boolean = false;

  // ============================================================================
  // COMPONENT STATE - Plan Selection
  // ============================================================================

  /** Currently selected subscription plan package with configured seats and pricing */
  selectedPlanPackage: SubscriptionPackageState | null = null;

  /** List of available plan packages based on selected applications */
  availablePlanPackages: SubscriptionPackageState[] = [];

  /** Current billing cycle selection */
  currentBillingCycle: BillingCycle;

  // ============================================================================
  // COMPONENT STATE - Add-ons
  // ============================================================================

  /** Available add-on services for the selected applications */
  availableAddOnServices: BillableServiceResponse[] = [];

  /** Map of selected add-on service IDs to their configuration (service + quantity) */
  selectedAddOnsWithQuantities: Map<
    number,
    { service: BillableServiceResponse; quantity: number }
  > = new Map();

  /** Whether to display the add-on selection section */
  shouldShowAddOnSelection: boolean = false;

  // ============================================================================
  // COMPONENT STATE - Trial Period
  // ============================================================================

  /** Whether trial period is enabled for this subscription */
  isTrialEnabled: boolean = false;

  /** Trial duration in days */
  trialDurationDays: number = 30;

  /** Calculated trial end date */
  trialEndDate: Date | null = null;

  // ============================================================================
  // COMPONENT STATE - Loading & UI
  // ============================================================================

  /** Whether plan packages are currently being loaded from the API */
  isLoadingPlanPackages: boolean = false;

  /** Whether add-on services are currently being loaded from the API */
  isLoadingAddOnServices: boolean = false;

  // ============================================================================
  // ENUMS (exposed for template access)
  // ============================================================================

  readonly BillingCycle = BillingCycle;
  readonly ChargeType = ChargeType;
  readonly Status = Status;

  // ============================================================================
  // CONSTRUCTOR & LIFECYCLE
  // ============================================================================

  constructor(
    private subscriptionPlanService: SubscriptionPlanService,
    private billableServiceService: BillableServiceService,
    private toastService: CustomToastService,
  ) {
    this.currentBillingCycle = this.initialBillingCycle;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to input properties
    if (changes['availableApplications'] && !changes['availableApplications'].firstChange) {
      this.workingApplicationsList = JSON.parse(JSON.stringify(this.availableApplications));
      // Clear selections when apps list changes
      this.selectedApplicationIds.clear();
    }

    // Only apply pre-selected apps on FIRST change to avoid infinite loop
    // Parent component may pass a method that returns the same array repeatedly
    if (
      changes['preSelectedApplicationIds'] &&
      changes['preSelectedApplicationIds'].firstChange &&
      this.preSelectedApplicationIds?.length > 0
    ) {
      this.applyPreSelectedApplications();
    }

    if (changes['initialBillingCycle'] && !changes['initialBillingCycle'].firstChange) {
      this.currentBillingCycle = this.initialBillingCycle;
    }
  }

  private initializeComponent(): void {
    this.workingApplicationsList = JSON.parse(JSON.stringify(this.availableApplications));
    this.currentBillingCycle = this.initialBillingCycle;
    this.trialDurationDays = this.defaultTrialDurationDays;

    // Apply pre-selected apps if provided
    if (this.preSelectedApplicationIds?.length > 0) {
      this.applyPreSelectedApplications();
    }

    // Auto-confirm if configured
    if (this.autoConfirmApplicationSelection && this.selectedApplicationIds.size > 0) {
      this.confirmApplicationSelectionAndLoadPlans();
    }
  }

  private applyPreSelectedApplications(): void {
    // Clear existing selections first to avoid accumulation
    this.selectedApplicationIds.clear();

    // Clear isAppSelect from all apps
    this.workingApplicationsList.forEach((app) => {
      app.isAppSelect = false;
    });

    // Apply new pre-selections
    this.preSelectedApplicationIds.forEach((appId) => {
      this.selectedApplicationIds.add(appId);
      const app = this.workingApplicationsList.find((a) => a.id === appId);
      if (app) {
        app.isAppSelect = true;
      }
    });
  }

  // ============================================================================
  // PUBLIC API METHODS (for parent component access)
  // ============================================================================

  /**
   * Gets the currently configured subscription plan with all customizations
   * @returns The selected plan package or null if none selected
   */
  public getConfiguredPlanPackage(): SubscriptionPackageState | null {
    return this.selectedPlanPackage;
  }

  /**
   * Calculates the final total price including base price, additional seats, and add-ons
   * @returns The total price
   */
  public calculateFinalTotalPrice(): number {
    const subtotal = this.calculateSubtotal();
    const savingsInfo = this.calculateSavingsInformation();
    const discountAmount = savingsInfo?.savedAmount || 0;

    return subtotal - discountAmount;
  }

  /**
   * Calculates the subtotal (base price + additional seats + add-ons, before discount)
   * @returns The subtotal price before discount
   */
  public calculateSubtotal(): number {
    const basePlanPrice = this.calculateTotalBasePriceOnly();
    const additionalSeatsCost = this.calculateAdditionalSeatsCost();
    const addOnsCost = this.calculateTotalAddOnCost();
    return basePlanPrice + additionalSeatsCost + addOnsCost;
  }

  /**
   * Gets the total number of seats across all applications
   * @returns Total seat count
   */
  public getTotalSeatsAcrossAllApplications(): number {
    if (!this.selectedPlanPackage) return 0;

    let totalSeats = 0;

    // Count app seats
    if (this.selectedPlanPackage.apps) {
      totalSeats += this.selectedPlanPackage.apps.reduce((sum, app) => sum + app.currentSeats, 0);
    }

    // Count addon seats
    if (this.selectedPlanPackage.addons) {
      totalSeats += this.selectedPlanPackage.addons.reduce(
        (sum, addon) => sum + addon.currentSeats,
        0,
      );
    }

    return totalSeats;
  }

  /**
   * Gets the list of selected add-ons with their quantities
   * @returns Array of add-on configurations
   */
  public getSelectedAddOnsWithQuantities(): Array<{
    service: BillableServiceResponse;
    quantity: number;
  }> {
    return Array.from(this.selectedAddOnsWithQuantities.values());
  }

  /**
   * Gets the current billing cycle
   * @returns The current billing cycle
   */
  public getCurrentBillingCycle(): BillingCycle {
    return this.currentBillingCycle;
  }

  /**
   * Resets the entire configuration to its initial state
   */
  public resetEntireConfiguration(): void {
    this.selectedPlanPackage = null;
    this.availablePlanPackages = [];
    this.selectedAddOnsWithQuantities.clear();
    this.availableAddOnServices = [];
    this.shouldShowAddOnSelection = false;
    this.areApplicationsConfirmedAndLocked = false;
    this.selectedApplicationIds.clear();

    this.workingApplicationsList.forEach((app) => {
      app.isAppSelect = false;
    });

    this.emitConfigurationValidity();
    this.configurationReset.emit();
  }

  /**
   * Checks if the current configuration is valid and complete
   * @returns True if a plan is selected with valid seat configuration
   */
  public isConfigurationValid(): boolean {
    return !!(
      this.selectedPlanPackage &&
      this.selectedPlanPackage.id &&
      this.selectedApplicationIds.size > 0
    );
  }

  // ============================================================================
  // APPLICATION SELECTION METHODS
  // ============================================================================

  /**
   * Toggles the selection state of an application
   * @param application The application to toggle
   */
  toggleApplicationSelection(application: AppLiteState): void {
    if (this.areApplicationsConfirmedAndLocked) return;

    application.isAppSelect = !application.isAppSelect;

    if (application.isAppSelect) {
      this.selectedApplicationIds.add(application.id);
    } else {
      this.selectedApplicationIds.delete(application.id);
    }

    this.emitConfigurationValidity();
  }

  /**
   * Gets the count of currently selected applications
   * @returns Number of selected apps
   */
  get selectedApplicationsCount(): number {
    return this.selectedApplicationIds.size;
  }

  /**
   * Checks if at least one application is selected
   * @returns True if one or more apps are selected
   */
  get hasSelectedApplications(): boolean {
    return this.selectedApplicationIds.size > 0;
  }

  /**
   * Confirms the application selection and loads available plans
   */
  async confirmApplicationSelectionAndLoadPlans(): Promise<void> {
    if (this.selectedApplicationIds.size === 0) {
      this.toastService.error('Please select at least one application');
      return;
    }

    this.areApplicationsConfirmedAndLocked = true;
    await this.loadAvailablePlanPackagesForSelectedApps();
  }

  /**
   * Allows editing the application selection (unlocks the selection)
   */
  unlockAndEditApplicationSelection(): void {
    this.areApplicationsConfirmedAndLocked = false;
    this.resetPlanSelectionOnly();
  }

  /**
   * Clears all application selections
   */
  clearAllApplicationSelections(): void {
    this.workingApplicationsList.forEach((app) => {
      app.isAppSelect = false;
    });
    this.selectedApplicationIds.clear();
    this.areApplicationsConfirmedAndLocked = false;
    this.resetPlanSelectionOnly();
    this.emitConfigurationValidity();
  }

  // ============================================================================
  // PLAN LOADING & SELECTION METHODS
  // ============================================================================

  /**
   * Loads available subscription plan packages for the currently selected applications
   */
  private async loadAvailablePlanPackagesForSelectedApps(): Promise<void> {
    const selectedAppIds = Array.from(this.selectedApplicationIds).join(',');
    if (!selectedAppIds) {
      this.toastService.error('No applications selected');
      return;
    }

    this.isLoadingPlanPackages = true;

    try {
      const response = await firstValueFrom(
        this.subscriptionPlanService.getAllPlans(selectedAppIds),
      );

      const packagesList: SubscriptionPackageResponse[] = JSON.parse(
        JSON.stringify(Array.isArray(response) ? response : []),
      );

      // Transform to state model with pre-calculated values
      const transformedPackages = packagesList.map((pkg) =>
        transformSubscriptionPackageToState(pkg, this.currentBillingCycle),
      );

      // Filter to only active packages
      this.availablePlanPackages = transformedPackages.filter(
        (pkg) => pkg.status === Status.Active,
      );

      // Load add-ons in parallel
      await this.loadAvailableAddOnServicesForSelectedApps(selectedAppIds);

      // Auto-select the pre-selected plan package (Manage Add-ons flow)
      if (this.preSelectedPlanPackage) {
        const matchingPlan = this.availablePlanPackages.find(
          (p) => p.id === this.preSelectedPlanPackage!.id,
        );
        if (matchingPlan) {
          // Overlay current seat values from the active subscription
          matchingPlan.apps = matchingPlan.apps.map((app) => {
            const activeApp = this.preSelectedPlanPackage!.apps?.find((a) => a.appId === app.appId);
            return activeApp ? { ...app, currentSeats: activeApp.currentSeats } : app;
          });
          matchingPlan.addons = (matchingPlan.addons || []).map((addon) => {
            const activeAddon = this.preSelectedPlanPackage!.addons?.find(
              (a) => a.billingServiceId === addon.billingServiceId,
            );
            return activeAddon ? { ...addon, currentSeats: activeAddon.currentSeats } : addon;
          });
          this.selectPlanPackage(matchingPlan);

          // Pre-tick any manual add-ons that were on the active subscription.
          // The same service can appear multiple times (e.g. two Online Store Integration entries).
          // Use quantity to track how many instances exist rather than overwriting with .set().
          const activeManualAddons = (this.preSelectedPlanPackage!.addons || []).filter(
            (a) => !a.isFromPackage,
          );
          for (const activeAddon of activeManualAddons) {
            const matchingService = this.availableAddOnServices.find(
              (s) => s.id === activeAddon.billingServiceId,
            );
            if (matchingService) {
              const existing = this.selectedAddOnsWithQuantities.get(matchingService.id);
              if (existing) {
                existing.quantity += 1;
              } else {
                this.selectedAddOnsWithQuantities.set(matchingService.id, {
                  service: matchingService,
                  quantity: 1,
                });
              }
            }
          }
          if (activeManualAddons.length > 0) {
            this.syncSelectedAddOnsToPackage();
            // Restore seat counts from the active subscription onto the freshly-synced manual addons.
            // syncSelectedAddOnsToPackage() builds new AddonState objects with currentSeats = 0
            // because no prior manual addons exist in selectedPlanPackage at this point.
            if (this.selectedPlanPackage) {
              this.selectedPlanPackage.addons = (this.selectedPlanPackage.addons || []).map(
                (addon) => {
                  if (!addon.isFromPackage) {
                    const activeAddon = activeManualAddons.find(
                      (a) => a.billingServiceId === addon.billingServiceId,
                    );
                    if (activeAddon && activeAddon.currentSeats > addon.includedSeats) {
                      return { ...addon, currentSeats: activeAddon.currentSeats };
                    }
                  }
                  return addon;
                },
              );
            }
            this.emitConfigurationIfComplete();
          }
        }
      }
    } catch (error) {
      console.error('Error loading subscription plan packages:', error);
      this.toastService.error('Failed to load subscription plans');
      this.availablePlanPackages = [];
    } finally {
      this.isLoadingPlanPackages = false;
    }
  }

  /**
   * Loads available add-on services for the selected applications
   */
  private async loadAvailableAddOnServicesForSelectedApps(appIds: string): Promise<void> {
    this.isLoadingAddOnServices = true;

    try {
      const addOns = await firstValueFrom(
        this.billableServiceService.getBillableServicesWithFilters(
          appIds,
          Status.Active,
          ServiceCategory.AddOn,
        ),
      );

      this.availableAddOnServices = addOns || [];
      this.shouldShowAddOnSelection = this.availableAddOnServices.length > 0;
    } catch (error) {
      console.error('Error loading add-on services:', error);
      this.availableAddOnServices = [];
      this.shouldShowAddOnSelection = false;
    } finally {
      this.isLoadingAddOnServices = false;
    }
  }

  /**
   * Selects a subscription plan package
   * @param planPackage The plan package to select
   */
  selectPlanPackage(planPackage: SubscriptionPackageState): void {
    // Clear any previously selected manual add-ons when changing plans
    this.selectedAddOnsWithQuantities.clear();

    // Reset trial configuration when changing plans
    this.isTrialEnabled = false;
    this.trialEndDate = null;
    this.trialDurationDays = this.defaultTrialDurationDays;

    // Deep clone to avoid mutating the original
    this.selectedPlanPackage = JSON.parse(JSON.stringify(planPackage));

    console.log('selectPlanPackage - Selected package:', {
      name: planPackage.name,
      id: planPackage.id,
      apps: this.selectedPlanPackage?.apps?.length || 0,
      addons: this.selectedPlanPackage?.addons?.length || 0,
      addonDetails:
        this.selectedPlanPackage?.addons?.map((a) => ({
          name: a.name,
          price: a.price,
          basePrice: a.basePrice,
          billingServiceId: a.billingServiceId,
          isFromPackage: a.isFromPackage,
        })) || [],
    });

    if (this.selectedPlanPackage) {
      this.selectedPlanPackage.isAssigned = true;
    }

    // Update the available plans list to reflect selection
    this.availablePlanPackages = this.availablePlanPackages.map((pkg) => ({
      ...pkg,
      isAssigned: pkg.id === planPackage.id,
    }));

    // Reload the full add-on list first, then filter out package-sourced addons
    const selectedAppIds = Array.from(this.selectedApplicationIds).join(',');
    this.loadAvailableAddOnServicesForSelectedApps(selectedAppIds).then(() => {
      // Filter out only the addons that come from the subscription package (isFromPackage: true).
      // Manual addons (isFromPackage: false) must remain visible so the user can add/remove them.
      const packageOnlyAddonIds =
        this.selectedPlanPackage?.addons
          ?.filter((a) => a.isFromPackage === true)
          ?.map((a) => a.billingServiceId) || [];
      if (packageOnlyAddonIds.length > 0) {
        this.availableAddOnServices = this.availableAddOnServices.filter(
          (service) => !packageOnlyAddonIds.includes(service.id),
        );
      }
    });

    // Sync selected add-ons into the package (will only include package addons now since we cleared manual ones)
    this.syncSelectedAddOnsToPackage();

    this.emitConfigurationValidity();
    this.emitConfigurationIfComplete();
  }

  /**
   * Resets only the plan selection (keeps app selection)
   */
  resetPlanSelectionOnly(): void {
    this.selectedPlanPackage = null;
    this.selectedAddOnsWithQuantities.clear();

    // Reset isAssigned state on all available plans but keep them visible
    this.availablePlanPackages = this.availablePlanPackages.map((pkg) => ({
      ...pkg,
      isAssigned: false,
    }));

    // Reload the full add-on list for selected apps to reset filtering
    const selectedAppIds = Array.from(this.selectedApplicationIds).join(',');
    if (selectedAppIds) {
      this.loadAvailableAddOnServicesForSelectedApps(selectedAppIds);
    } else {
      this.availableAddOnServices = [];
      this.shouldShowAddOnSelection = false;
    }

    // Reset trial configuration to defaults
    this.isTrialEnabled = false;
    this.trialEndDate = null;
    this.trialDurationDays = this.defaultTrialDurationDays;

    this.emitConfigurationValidity();
    this.configurationReset.emit();
  }

  // ============================================================================
  // BILLING CYCLE METHODS
  // ============================================================================

  /**
   * Changes the billing cycle and recalculates all pricing
   * @param newBillingCycle The new billing cycle to apply
   */
  changeBillingCycle(newBillingCycle: BillingCycle): void {
    this.currentBillingCycle = Number(newBillingCycle);

    // Re-transform all available plans with the new billing cycle
    if (this.availablePlanPackages.length > 0) {
      this.availablePlanPackages = this.availablePlanPackages.map((plan) => {
        const transformed = transformSubscriptionPackageToState(plan, this.currentBillingCycle);
        transformed.isAssigned = plan.isAssigned;
        return transformed;
      });
    }

    // Update the selected plan if one exists
    if (this.selectedPlanPackage && this.selectedPlanPackage.id) {
      // Preserve current seat selections for apps
      const currentSeatsMap = new Map(
        this.selectedPlanPackage.apps.map((app) => [app.appId, app.currentSeats]),
      );

      // Preserve current seat selections for addons
      const addonSeatsMap = new Map(
        this.selectedPlanPackage.addons?.map((addon) => [addon.id, addon.currentSeats]) || [],
      );

      // Re-transform the selected plan
      this.selectedPlanPackage = transformSubscriptionPackageToState(
        this.selectedPlanPackage,
        this.currentBillingCycle,
      );
      this.selectedPlanPackage.isAssigned = true;

      // Restore preserved seat counts for apps
      this.selectedPlanPackage.apps.forEach((app) => {
        const preservedSeats = currentSeatsMap.get(app.appId);
        if (preservedSeats !== undefined) {
          app.currentSeats = preservedSeats;
        }
      });

      // Resync selected addons with new billing cycle prices and restore seat counts
      this.syncSelectedAddOnsToPackage();
      if (this.selectedPlanPackage.addons) {
        this.selectedPlanPackage.addons.forEach((addon) => {
          const preservedSeats = addonSeatsMap.get(addon.id);
          if (preservedSeats !== undefined) {
            addon.currentSeats = preservedSeats;
          }
        });
      }

      // Emit updated configuration with new billing cycle pricing
      this.emitConfigurationIfComplete();
    }

    this.billingCycleChanged.emit(this.currentBillingCycle);
  }

  /**
   * Gets a human-readable label for the current billing cycle
   * @returns Billing cycle display name
   */
  getBillingCycleDisplayName(): string {
    switch (this.currentBillingCycle) {
      case BillingCycle.Monthly:
        return 'Monthly';
      case BillingCycle.Yearly_PaidMonthly:
        return 'Yearly (Paid Monthly)';
      case BillingCycle.Yearly_PaidYearly:
        return 'Yearly (Paid Yearly)';
      default:
        return 'Monthly';
    }
  }

  /**
   * Gets the period label for pricing display (/mo or /yr)
   * @returns Period label string
   */
  getBillingPeriodLabel(): string {
    return this.currentBillingCycle === BillingCycle.Yearly_PaidYearly ? '/yr' : '/mo';
  }

  /**
   * Gets the display price for a plan based on current billing cycle
   * @param plan The subscription package to get price for
   * @returns The price to display
   */
  getPlanDisplayPrice(plan: SubscriptionPackageState): number {
    if (!plan.pricingTiers) {
      return plan.monthlyPrice || 0;
    }

    switch (this.currentBillingCycle) {
      case BillingCycle.Monthly:
        return (
          plan.pricingTiers.monthly?.priceAfterDiscount ||
          plan.pricingTiers.monthly?.pricePerMonth ||
          0
        );

      case BillingCycle.Yearly_PaidMonthly:
        return (
          plan.pricingTiers.yearlyPaidMonthly?.priceAfterDiscount ||
          plan.pricingTiers.yearlyPaidMonthly?.pricePerMonth ||
          0
        );

      case BillingCycle.Yearly_PaidYearly:
        return (
          plan.pricingTiers.yearlyPaidYearly?.priceAfterDiscount ||
          plan.pricingTiers.yearlyPaidYearly?.priceTotal ||
          0
        );

      default:
        return plan.monthlyPrice || 0;
    }
  }

  /** Whether the plan has a discount for the current billing cycle */
  getPlanHasDiscount(plan: SubscriptionPackageState): boolean {
    return this.getCurrentPricingTier(plan)?.hasDiscount ?? false;
  }

  /** Gets the price before discount for the current billing cycle */
  getPlanOriginalPrice(plan: SubscriptionPackageState): number {
    return this.getCurrentPricingTier(plan)?.priceBeforeDiscount || 0;
  }

  /** Gets the discount percentage for the current billing cycle */
  getPlanDiscountPercent(plan: SubscriptionPackageState): number {
    return this.getCurrentPricingTier(plan)?.discountPercent ?? 0;
  }

  // ============================================================================
  // SEAT CONFIGURATION METHODS
  // ============================================================================

  /**
   * Updates the number of seats for a specific application
   * @param appId The application ID
   * @param newSeatCount The new seat count
   */
  updateSeatsForApplication(appId: number, newSeatCount: number): void {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.apps) return;

    const app = this.selectedPlanPackage.apps.find((a) => a.appId === appId);
    if (app) {
      const minimumSeats = app.includedSeats;
      const newSeats = Number(newSeatCount);

      // Validate that seats are not less than included seats
      if (newSeats < minimumSeats) {
        this.toastService.clientError(
          `Seats cannot be less than ${minimumSeats} (included seats for ${app.appName})`,
        );
        app.currentSeats = minimumSeats;
      } else {
        app.currentSeats = newSeats || minimumSeats;
      }
    }

    this.emitConfigurationIfComplete();
  }

  /**
   * Gets the breakdown of additional seats cost by application
   * @returns Array of seat cost breakdown by app
   */
  getAdditionalSeatsBreakdownByApplication(): Array<{
    appName: string;
    additionalSeats: number;
    perSeatPrice: number;
    totalCost: number;
  }> {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.apps) return [];

    return this.selectedPlanPackage.apps
      .filter((app) => !this.isRevenueSharing(app)) // Exclude revenue sharing apps
      .map((app) => {
        const additionalSeats = Math.max(0, app.currentSeats - app.includedSeats);
        return {
          appName: app.appName,
          additionalSeats,
          perSeatPrice: app.perSeatPrice,
          totalCost: additionalSeats * app.perSeatPrice,
        };
      })
      .filter((item) => item.additionalSeats > 0);
  }

  /**
   * Gets the breakdown of additional seats cost for add-ons
   * @returns Array of seat cost breakdown by addon
   */
  getAdditionalSeatsBreakdownByAddon(): Array<{
    addonName: string;
    additionalSeats: number;
    perSeatPrice: number;
    totalCost: number;
  }> {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.addons) return [];

    return this.getSortedAddons(this.selectedPlanPackage.addons)
      .map((addon) => {
        const additionalSeats = Math.max(0, addon.currentSeats - addon.includedSeats);
        return {
          addonName: addon.name,
          additionalSeats,
          perSeatPrice: addon.perSeatPrice,
          totalCost: additionalSeats * addon.perSeatPrice,
        };
      })
      .filter((item) => item.additionalSeats > 0);
  }

  // ============================================================================
  // ADD-ON MANAGEMENT METHODS
  // ============================================================================

  /**
   * Toggles the selection state of an add-on service
   * @param addOnService The add-on service to toggle
   */
  toggleAddOnSelection(addOnService: BillableServiceResponse): void {
    if (this.selectedAddOnsWithQuantities.has(addOnService.id)) {
      this.selectedAddOnsWithQuantities.delete(addOnService.id);
    } else {
      this.selectedAddOnsWithQuantities.set(addOnService.id, {
        service: addOnService,
        quantity: 1,
      });
    }

    this.syncSelectedAddOnsToPackage();
    this.emitConfigurationIfComplete();
  }

  /**
   * Checks if a specific add-on is currently selected
   * @param addOnId The add-on service ID
   * @returns True if the add-on is selected
   */
  isAddOnSelected(addOnId: number): boolean {
    return this.selectedAddOnsWithQuantities.has(addOnId);
  }

  /**
   * Updates the quantity for a selected add-on
   * @param addOnId The add-on service ID
   * @param newQuantity The new quantity
   */
  updateAddOnQuantity(addOnId: number, newQuantity: number): void {
    const addOnConfig = this.selectedAddOnsWithQuantities.get(addOnId);

    if (addOnConfig && newQuantity > 0) {
      addOnConfig.quantity = newQuantity;
    } else if (newQuantity <= 0) {
      this.selectedAddOnsWithQuantities.delete(addOnId);
    }

    this.syncSelectedAddOnsToPackage();
    this.emitConfigurationIfComplete();
  }

  /**
   * Gets the current quantity for a specific add-on
   * @param addOnId The add-on service ID
   * @returns The quantity (0 if not selected)
   */
  getAddOnQuantity(addOnId: number): number {
    return this.selectedAddOnsWithQuantities.get(addOnId)?.quantity || 0;
  }

  /**
   * Syncs selected add-ons from selectedAddOnsWithQuantities into selectedPlanPackage.addons
   * This enables seat configuration for add-ons with per-seat charges
   * IMPORTANT: Preserves package addons (isFromPackage: true) and merges with manual addons
   */
  private syncSelectedAddOnsToPackage(): void {
    if (!this.selectedPlanPackage) return;

    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    // STEP 1: Preserve existing package addons (those with isFromPackage: true)
    const packageAddons =
      this.selectedPlanPackage.addons?.filter((a) => a.isFromPackage === true) || [];

    // STEP 1.5: Preserve current seat values from existing manual addons
    const existingManualAddons =
      this.selectedPlanPackage.addons?.filter((a) => a.isFromPackage === false) || [];
    const currentSeatsMap = new Map(
      existingManualAddons.map((addon) => [addon.billingServiceId, addon.currentSeats]),
    );

    // STEP 2: Convert selected manual add-ons to AddonState objects
    const manualAddonStates: AddonState[] = [];
    let displayOrder = (this.selectedPlanPackage.apps?.length || 0) + packageAddons.length + 1;

    this.selectedAddOnsWithQuantities.forEach((config) => {
      const service = config.service;
      const allCharges = service.billableServiceCharges || [];

      // Filter to only include Flat and PerSeat charges for pricing (PerSeat only if value > 0)
      const pricingCharges = allCharges.filter(
        (c) =>
          Number(c.type) === ChargeType.Flat ||
          (Number(c.type) === ChargeType.PerSeat && c.value > 0),
      );

      const baseCharge = pricingCharges.find((c) => Number(c.type) === ChargeType.Flat);
      const perSeatCharge = pricingCharges.find((c) => Number(c.type) === ChargeType.PerSeat);

      // Base price only from Flat charge - PerSeat doesn't contribute to base
      const basePrice = baseCharge?.value || 0;
      const perSeatPrice = perSeatCharge?.value || 0;
      const includedSeats = perSeatPrice > 0 ? 0 : 0;

      // Restore previously set seat value, or use includedSeats as default
      const preservedSeats = currentSeatsMap.get(service.id);
      const currentSeats = preservedSeats !== undefined ? preservedSeats : includedSeats;

      // Create one AddonState per unit of quantity (handles duplicate service instances
      // e.g. the same add-on appearing multiple times in an active subscription).
      for (let i = 0; i < config.quantity; i++) {
        manualAddonStates.push({
          id: service.id,
          name: service.name || '',
          code: service.code || '',
          price: basePrice * multiplier,
          priceMonthly: basePrice,
          basePrice: basePrice * multiplier,
          billingServiceId: service.id,
          category: service.category || ServiceCategory.AddOn,
          displayOrder: displayOrder++,
          includedSeats: includedSeats,
          currentSeats: currentSeats,
          perSeatPrice: perSeatPrice * multiplier,
          perSeatPriceMonthly: perSeatPrice,
          charges: allCharges.map((c) => ({
            id: c.id,
            subscriptionPackageItemId: 0, // Manual addon, no package item yet
            billingServiceChargeId: c.id,
            snapshotOfValue: c.value,
            value: c.value,
            includeSeatsInBasePrice: 0,
            type: c.type,
          })),
          isFromPackage: false, // Manually-added addon
        });
      }
    });

    // STEP 3: Merge package addons with manual addons
    this.selectedPlanPackage.addons = [...packageAddons, ...manualAddonStates];
  }

  /**
   * Checks if an add-on service has per-seat charges
   * @param addOn The add-on service to check
   * @returns True if the add-on has per-seat pricing
   */
  hasPerSeatChargeForAddOnService(addOn: BillableServiceResponse): boolean {
    return (
      addOn.billableServiceCharges?.some(
        (charge) => charge.type === ChargeType.PerSeat && charge.value > 0,
      ) || false
    );
  }

  /**
   * Gets the per-seat price for an add-on service
   * @param addOn The add-on service
   * @returns The per-seat price or 0 if not found
   */
  getPerSeatPriceForAddOnService(addOn: BillableServiceResponse): number {
    const perSeatCharge = addOn.billableServiceCharges?.find(
      (charge) => charge.type === ChargeType.PerSeat && charge.value > 0,
    );
    return perSeatCharge?.value || 0;
  }

  /**
   * Gets the base price for an add-on service (from Flat charge only)
   * @param addOn The add-on service
   * @returns The base price or 0 if not found
   */
  getBasePriceForAddOnService(addOn: BillableServiceResponse): number {
    const flatCharge = addOn.billableServiceCharges?.find(
      (charge) => charge.type === ChargeType.Flat,
    );
    return flatCharge?.value || 0;
  }

  /**
   * Gets detailed add-on information for display
   * @returns Array of add-on display information
   */
  getDetailedAddOnsList(): AddonDisplayInfo[] {
    const addOnsList: AddonDisplayInfo[] = [];
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const priceMultiplier = isYearlyPaidAnnually ? 12 : 1;

    this.selectedAddOnsWithQuantities.forEach((config) => {
      // Only use Flat charge for unit price
      const flatCharge = config.service.billableServiceCharges?.find(
        (c) => Number(c.type) === ChargeType.Flat,
      );
      const unitPrice = flatCharge?.value || 0;

      addOnsList.push({
        name: config.service.name || 'Unknown Add-on',
        unitPrice: unitPrice * priceMultiplier,
        quantity: config.quantity,
        totalPrice: unitPrice * priceMultiplier * config.quantity,
      });
    });

    return addOnsList;
  }

  /**
   * Clears all manually selected add-ons (keeps package-included add-ons)
   */
  clearAllAddOns(): void {
    this.selectedAddOnsWithQuantities.clear();

    // Sync changes to package to remove manual add-ons while keeping package add-ons
    this.syncSelectedAddOnsToPackage();

    this.emitConfigurationIfComplete();
  }

  // ============================================================================
  // PRICING CALCULATION METHODS
  // ============================================================================

  /**
   * Calculates the base price (sum of all app base prices only)
   * Note: Addon costs are calculated separately in calculateTotalAddOnCost()
   * @returns Total base price for apps only
   */
  calculateTotalBasePriceOnly(): number {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.apps) return 0;

    // Only calculate app base prices (addons are calculated in calculateTotalAddOnCost)
    return this.selectedPlanPackage.apps.reduce((sum, app) => sum + app.basePrice, 0);
  }

  /**
   * Calculates the cost of additional seats beyond included seats for APPS only
   * Note: Excludes apps with revenue sharing (e.g., Lister)
   * Note: Addon seats are calculated in calculateTotalAddOnCost(), not here
   * @returns Total additional seats cost for apps
   */
  calculateAdditionalSeatsCost(): number {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.apps) return 0;

    // Calculate ONLY app additional seats (addons are handled in calculateTotalAddOnCost)
    return this.selectedPlanPackage.apps.reduce((sum, app) => {
      // Skip apps that use revenue sharing instead of per-seat pricing
      if (this.isRevenueSharing(app)) {
        return sum;
      }
      const additionalSeats = Math.max(0, app.currentSeats - app.includedSeats);
      return sum + additionalSeats * app.perSeatPrice;
    }, 0);
  }

  /**
   * Checks if an app uses revenue sharing instead of per-seat pricing
   * @param app The application to check
   * @returns True if the app uses revenue sharing
   */
  isRevenueSharing(app: AppAccessState): boolean {
    if (!app.charges || app.charges.length === 0) return false;

    // Check if any charge has type PercentageOfRevenue
    return app.charges.some((charge: any) => charge.type === ChargeType.PercentageOfRevenue);
  }

  /**
   * Gets the revenue sharing percentage for an app
   * @param app The application
   * @returns Revenue sharing percentage or null
   */
  getRevenueSharingPercentage(app: AppAccessState): number | null {
    if (!app.charges || app.charges.length === 0) return null;

    const revenueCharge = app.charges.find(
      (charge: any) => charge.type === ChargeType.PercentageOfRevenue,
    );
    return revenueCharge?.value || null;
  }

  /**
   * Calculates the total cost of all selected add-ons
   * Includes both package addons and manually selected addons
   * Dynamically calculates based on current seats (basePrice + extra seats cost)
   * @returns Total add-ons cost
   */
  calculateTotalAddOnCost(): number {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.addons) return 0;

    // Calculate from ALL addons in the package (both package and manual)
    return this.selectedPlanPackage.addons.reduce((total, addon) => {
      // Calculate total price: base price + additional seats cost
      const additionalSeats = Math.max(0, addon.currentSeats - addon.includedSeats);
      const totalPrice = addon.basePrice + additionalSeats * addon.perSeatPrice;
      return total + totalPrice;
    }, 0);
  }

  /**
   * Gets savings information from billing cycle discounts
   * Discount is calculated as a percentage of the subtotal (base price + additional seats + add-ons)
   * @returns Savings info or null if no discount
   */
  calculateSavingsInformation(): {
    savedAmount: number;
    savedValue: number;
    isPercentage: boolean;
  } | null {
    if (!this.selectedPlanPackage) return null;

    const pricingTier = this.getCurrentPricingTier(this.selectedPlanPackage);
    if (!pricingTier || !pricingTier.hasDiscount) {
      return null;
    }

    const isPercentage = pricingTier.discountType === DiscountType.Percentage;
    let savedAmount = 0;

    if (isPercentage && pricingTier.discountPercent > 0) {
      // For percentage discounts, apply the percentage to the SUBTOTAL (including add-ons and extra seats)
      const subtotal = this.calculateSubtotal();
      savedAmount = subtotal * (pricingTier.discountPercent / 100);
    } else if (pricingTier.discountType === DiscountType.Amount && pricingTier.discountAmount > 0) {
      // For fixed amount discounts, use the flat discount amount
      savedAmount = pricingTier.discountAmount;
    } else {
      return null;
    }

    return {
      // Flat dollar amount actually deducted from the total
      savedAmount: savedAmount,
      // Display value: percentage number (e.g. 20) or flat dollar amount (e.g. 10)
      savedValue: isPercentage
        ? Math.round(pricingTier.discountPercent * 100) / 100
        : pricingTier.discountAmount,
      isPercentage,
    };
  }

  /**
   * Gets the current pricing tier based on the billing cycle
   * @param planPackage The plan package
   * @returns The current pricing tier or null
   */
  private getCurrentPricingTier(planPackage: SubscriptionPackageState): PricingTier | null {
    if (!planPackage || !planPackage.pricingTiers) return null;

    switch (this.currentBillingCycle) {
      case BillingCycle.Monthly:
        return planPackage.pricingTiers.monthly;
      case BillingCycle.Yearly_PaidMonthly:
        return planPackage.pricingTiers.yearlyPaidMonthly;
      case BillingCycle.Yearly_PaidYearly:
        return planPackage.pricingTiers.yearlyPaidYearly;
      default:
        return planPackage.pricingTiers.monthly;
    }
  }

  /**
   * Gets the base price breakdown by application
   * @returns Array of base prices by app
   */
  getBasePriceBreakdownByApplication(): AppBasePriceInfo[] {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.apps) return [];

    return this.getSortedApps(this.selectedPlanPackage.apps).map((app) => ({
      appName: app.appName,
      basePrice: app.basePrice,
    }));
  }

  /**
   * Gets detailed charge breakdown by application including all charge types
   * Shows which charges are included in total calculation vs display-only
   */
  getDetailedChargeBreakdownByApplication(): Array<{
    appName: string;
    charges: Array<{
      type: number;
      typeName: string;
      value: number;
      includedInTotal: boolean;
    }>;
  }> {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.apps) return [];

    // Apply billing cycle multiplier to charge values
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    return this.getSortedApps(this.selectedPlanPackage.apps).map((app) => ({
      appName: app.appName,
      charges: (app.charges || [])
        .filter((chargeItem) => chargeItem.type !== ChargeType.PerSeat)
        .map((charge: any) => {
          // Only Flat and PerSeat (with value > 0) are included in total
          const isFlat = charge.type === ChargeType.Flat;
          const isPerStore = charge.type === ChargeType.PerStore;
          const includedInTotal = isFlat;

          return {
            type: charge.type,
            typeName: this.getChargeTypeName(charge.type),
            value:
              charge.type == ChargeType.PerSeat || charge.type == ChargeType.Flat || isPerStore
                ? (charge.value || 0) * multiplier
                : charge.value || 0,
            includedInTotal: includedInTotal,
          };
        }),
    }));
  }

  /**
   * Gets the base price breakdown by addon
   * @returns Array of base prices by addon
   */
  getBasePriceBreakdownByAddon(): Array<{ addonName: string; basePrice: number }> {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.addons) return [];
    const sortedAddons = this.getSortedAddons(this.selectedPlanPackage.addons);
    const breakdown = sortedAddons
      .map((addon) => ({
        addonName: addon.name,
        basePrice: addon.price,
        isFromPackage: addon.isFromPackage,
      }))
      // Show package addons even if price is 0, but filter out manual addons with 0 price
      .filter((item) => item.basePrice > 0 || item.isFromPackage === true);

    return breakdown;
  }

  /**
   * Gets detailed charge breakdown by addon including all charge types
   * Shows which charges are included in total calculation vs display-only
   */
  getDetailedChargeBreakdownByAddon(): Array<{
    addonName: string;
    charges: Array<{
      type: number;
      typeName: string;
      value: number;
      includedInTotal: boolean;
    }>;
  }> {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.addons) return [];

    // Apply billing cycle multiplier to charge values
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    return this.getSortedAddons(this.selectedPlanPackage.addons).map((addon) => ({
      addonName: addon.name,
      charges: (addon.charges || [])
        .filter((chargeItem) => chargeItem.type !== ChargeType.PerSeat)
        .map((charge: any) => {
          // Only Flat and PerSeat (with value > 0) are included in total
          const isFlat = charge.type === ChargeType.Flat;
          const isPerStore = charge.type === ChargeType.PerStore;
          const includedInTotal = isFlat;

          return {
            type: charge.type,
            typeName: this.getChargeTypeName(charge.type),
            value:
              charge.type == ChargeType.PerSeat || charge.type == ChargeType.Flat || isPerStore
                ? (charge.value || 0) * multiplier
                : charge.value || 0,
            includedInTotal: includedInTotal,
          };
        }),
    }));
  }

  /**
   * Gets human-readable charge type name
   */
  getChargeTypeName(chargeType: number): string {
    switch (chargeType) {
      case ChargeType.Flat:
        return 'Flat Fee';
      case ChargeType.PerSeat:
        return 'Per Seat';
      case ChargeType.PerStore:
        return 'Per Store';
      case ChargeType.PercentageOfRevenue:
        return 'Revenue Share';
      case ChargeType.PerTransaction:
        return 'Per Transaction';
      case ChargeType.Unknown:
      default:
        return 'Other Charge';
    }
  }

  /**
   * Gets the pricing label for an add-on based on charge type priority
  * Priority: Flat Fee > Per Seat > Per Store > Per Transaction > Revenue
   * @param addon The add-on to get the pricing label for
   * @returns Pricing label string (e.g., '/mo', '/per seat', '/per transaction')
   */
  getAddonPricingLabel(addon: AddonState): string {
    if (!addon.charges || addon.charges.length === 0) {
      return this.getBillingPeriodLabel();
    }

    const chargeTypes = addon.charges.map((c) => Number(c.type));

    // Priority 1: Flat Fee
    if (chargeTypes.includes(ChargeType.Flat)) {
      return this.getBillingPeriodLabel();
    }

    // Priority 2: Per Seat
    if (chargeTypes.includes(ChargeType.PerSeat)) {
      return '/per seat';
    }

    // Priority 3: Per Store
    if (chargeTypes.includes(ChargeType.PerStore)) {
      return '/per store';
    }

    // Priority 4: Per Transaction
    if (chargeTypes.includes(ChargeType.PerTransaction)) {
      return '/per transaction';
    }

    // Priority 5: Revenue (PercentageOfRevenue)
    if (chargeTypes.includes(ChargeType.PercentageOfRevenue)) {
      return this.getBillingPeriodLabel();
    }

    // Default fallback
    return this.getBillingPeriodLabel();
  }

  /**
   * Checks if addon price should be displayed as percentage (for Revenue Share)
   * @param addon The add-on to check
   * @returns True if should display as percentage
   */
  isAddonPricePercentage(addon: AddonState): boolean {
    if (!addon.charges || addon.charges.length === 0) {
      return false;
    }

    const chargeTypes = addon.charges.map((c) => Number(c.type));

    // Only show as percentage if ONLY Revenue charge exists (no Flat, PerSeat, PerStore, or PerTransaction)
    const hasFlat = chargeTypes.includes(ChargeType.Flat);
    const hasPerSeat = chargeTypes.includes(ChargeType.PerSeat);
    const hasPerStore = chargeTypes.includes(ChargeType.PerStore);
    const hasPerTransaction = chargeTypes.includes(ChargeType.PerTransaction);
    const hasRevenue = chargeTypes.includes(ChargeType.PercentageOfRevenue);

    return hasRevenue && !hasFlat && !hasPerSeat && !hasPerStore && !hasPerTransaction;
  }

  /**
   * Returns ALL charges for a plan addon (AddonState) as display-ready value/label pairs.
   */
  getAddonAllChargesForDisplay(addon: AddonState): Array<{ value: string; label: string }> {
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;
    const periodLabel = this.getBillingPeriodLabel();

    if (!addon.charges || addon.charges.length === 0) {
      const base = (addon.basePrice || 0) * multiplier;
      return [{ value: `$${base.toFixed(2)}`, label: periodLabel }];
    }

    const result: Array<{ value: string; label: string }> = [];
    for (const c of addon.charges as any[]) {
      const type = Number(c.type);
      const val = c.value || 0;
      if (val === 0) continue;
      switch (type) {
        case ChargeType.Flat:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: periodLabel });
          break;
        case ChargeType.PerSeat:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: '/seat' });
          break;
        case ChargeType.PerStore:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: '/store' });
          break;
        case ChargeType.PerTransaction:
          result.push({ value: `$${val.toFixed(2)}`, label: '/transaction' });
          break;
        case ChargeType.PercentageOfRevenue:
          result.push({ value: `${val.toFixed(2)}%`, label: 'of revenue' });
          break;
        default:
          result.push({ value: `$${val.toFixed(2)}`, label: '' });
      }
    }
    return result.length > 0 ? result : [{ value: '$0.00', label: periodLabel }];
  }

  /**
   * Returns ALL charges for an available add-on (BillableServiceResponse) as display-ready value/label pairs.
   * Used in the add-on selection card price corner.
   */
  getAddOnAllChargesForDisplay(
    addOn: BillableServiceResponse,
  ): Array<{ value: string; label: string }> {
    if (!addOn.billableServiceCharges || addOn.billableServiceCharges.length === 0) return [];

    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;
    const periodLabel = this.getBillingPeriodLabel();

    const result: Array<{ value: string; label: string }> = [];
    for (const c of addOn.billableServiceCharges) {
      const type = Number(c.type);
      const val = c.value || 0;
      if (val === 0) continue;
      switch (type) {
        case ChargeType.Flat:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: periodLabel });
          break;
        case ChargeType.PerSeat:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: '/seat' });
          break;
        case ChargeType.PerStore:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: '/store' });
          break;
        case ChargeType.PerTransaction:
          result.push({ value: `$${val.toFixed(2)}`, label: '/transaction' });
          break;
        case ChargeType.PercentageOfRevenue:
          result.push({ value: `${val.toFixed(2)}%`, label: 'of revenue' });
          break;
      }
    }
    return result;
  }

  /**
   * Formats charge value based on charge type
   * Revenue Share shows as percentage, others show as currency
   */
  formatChargeValue(value: number, chargeType: number): string {
    if (chargeType === ChargeType.PercentageOfRevenue) {
      return `${value.toFixed(2)}%`;
    }
    return `$${value.toFixed(2)}`;
  }

  // ============================================================================
  // UTILITY & HELPER METHODS
  // ============================================================================

  /**
   * Gets the icon URL for a specific application
   * @param appId The application ID
   * @returns The icon URL
   */
  getApplicationIcon(appId: number): string {
    // First try to find the app in workingApplicationsList
    const app = this.workingApplicationsList.find((a) => a.id === appId);
    if (app?.appIcon) {
      return app.appIcon;
    }

    // Then try availableApplications
    const availableApp = this.availableApplications.find((a) => a.id === appId);
    if (availableApp?.appIcon) {
      return availableApp.appIcon;
    }

    // Fallback to default icon
    return 'assets/images/navigation/default-app.svg';
  }

  /**
   * Gets a color for displaying add-ons (for visual variety)
   * @param index The index of the add-on
   * @returns A color hex code
   */
  getAddOnDisplayColor(index: number): string {
    const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];
    return colors[index % colors.length];
  }

  /**
   * Checks if any app or addon in a plan has additional seat pricing
   * @param planPackage The plan package to check
   * @returns True if at least one app or addon has per-seat pricing
   */
  hasAdditionalSeatPricing(planPackage: SubscriptionPackageState): boolean {
    const hasAppSeats = planPackage.apps?.some((app) => app.perSeatPrice > 0) || false;
    const hasAddonSeats = planPackage.addons?.some((addon) => addon.perSeatPrice > 0) || false;
    return hasAppSeats || hasAddonSeats;
  }

  /**
   * Checks if a specific app has per-seat charge
   * @param app The app to check
   * @returns True if the app has per-seat pricing
   */
  hasPerSeatCharge(app: AppAccessState): boolean {
    return app.perSeatPrice > 0 || app.perSeatPriceMonthly > 0;
  }

  /**
   * Gets sorted apps for consistent display across all plan cards
   * @param apps The apps to sort
   * @returns Sorted array of apps
   */
  getSortedApps(apps: AppAccessState[]): AppAccessState[] {
    if (!apps || apps.length === 0) return [];
    return [...apps].sort((a, b) => a.appName.localeCompare(b.appName));
  }

  /**
   * Gets sorted add-ons for consistent display across all plan cards
   * @param addons The add-ons to sort
   * @returns Sorted array of add-ons
   */
  getSortedAddons(addons: AddonState[]): AddonState[] {
    if (!addons || addons.length === 0) return [];
    return [...addons].sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Checks if a specific addon has per-seat charge
   * @param addon The addon to check
   * @returns True if the addon has per-seat pricing
   */
  hasPerSeatChargeAddon(addon: AddonState): boolean {
    return addon.perSeatPrice > 0 || addon.perSeatPriceMonthly > 0;
  }

  /**
   * Updates the number of seats for a specific addon
   * @param addonId The addon ID
   * @param newSeatCount The new seat count
   */
  updateSeatsForAddon(addonId: number, newSeatCount: number): void {
    if (!this.selectedPlanPackage || !this.selectedPlanPackage.addons) return;

    const addon = this.selectedPlanPackage.addons.find((a) => a.id === addonId);
    if (addon) {
      const minimumSeats = addon.includedSeats;
      const newSeats = Number(newSeatCount);

      // Validate that seats are not less than included seats
      if (newSeats < minimumSeats) {
        this.toastService.clientError(
          `Seats cannot be less than ${minimumSeats} (included seats for ${addon.name})`,
        );
        addon.currentSeats = minimumSeats;
      } else {
        addon.currentSeats = newSeats || minimumSeats;
      }
    }

    this.emitConfigurationIfComplete();
  }

  /**
   * TrackBy function for ngFor optimization
   */
  trackByApplicationId(index: number, app: AppLiteState): number {
    return app.id;
  }

  /**
   * TrackBy function for plan packages
   */
  trackByPlanPackageId(index: number, plan: SubscriptionPackageState): number {
    return plan.id;
  }

  /**
   * TrackBy function for add-on services
   */
  trackByAddOnId(index: number, addOn: BillableServiceResponse): number {
    return addOn.id;
  }

  // ============================================================================
  // PRIVATE EVENT EMITTER HELPERS
  // ============================================================================

  /**
   * Emits the current configuration validity state
   */
  private emitConfigurationValidity(): void {
    const isValid = this.isConfigurationValid();
    this.configurationValidityChanged.emit(isValid);
  }

  /**
   * Emits the complete configuration if it's valid and complete
   */
  private emitConfigurationIfComplete(): void {
    if (this.isConfigurationValid()) {
      const trialEndDateString =
        this.isTrialEnabled && this.trialEndDate ? this.formatDateToISO(this.trialEndDate) : null;

      console.log('Emitting subscription configuration:', {
        isTrialEnabled: this.isTrialEnabled,
        trialEndDate: this.trialEndDate,
        trialEndDateString: trialEndDateString,
        billingCycle: this.currentBillingCycle,
      });

      this.subscriptionPlanConfigured.emit({
        plan: this.selectedPlanPackage!,
        billingCycle: this.currentBillingCycle,
        totalPrice: this.calculateFinalTotalPrice(),
        totalSeats: this.getTotalSeatsAcrossAllApplications(),
        trialEnabled: this.isTrialEnabled,
        trialEndDate: trialEndDateString,
      });
    }
  }

  // ============================================================================
  // TRIAL PERIOD METHODS
  // ============================================================================

  /**
   * Toggles trial period on/off
   */
  toggleTrialPeriod(enabled: boolean): void {
    this.isTrialEnabled = enabled;
    if (enabled) {
      this.calculateTrialEndDate();
    } else {
      this.trialEndDate = null;
    }
    this.emitConfigurationIfComplete();
  }

  /**
   * Updates trial duration and recalculates end date
   */
  updateTrialDuration(days: number): void {
    this.trialDurationDays = days;
    if (this.isTrialEnabled) {
      this.calculateTrialEndDate();
      this.emitConfigurationIfComplete();
    }
  }

  /**
   * Calculates trial end date based on current date + trial duration.
   * Today counts as day 1, so end date = today + (days - 1).
   * Example: 30-day trial starting March 31 ends April 29.
   */
  private calculateTrialEndDate(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to midnight
    this.trialEndDate = new Date(
      today.getTime() + (this.trialDurationDays - 1) * 24 * 60 * 60 * 1000,
    );
  }

  /**
   * Formats date to ISO string (YYYY-MM-DD)
   */
  private formatDateToISO(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Gets remaining trial days from now until trial end
   */
  getRemainingTrialDays(): number {
    if (!this.trialEndDate) return this.trialDurationDays;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = this.trialEndDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
}
